// Aqualux Nexus Pool Shop Water Tester — manufacturer build spec data
// Architecture: Probe block + dual optical strip readers (no liquid reagents / cuvettes / dosing pumps)

export const DIM = {
  w: 260, h: 300, d: 240, // mm — proposed true-size envelope (to be validated in DFM)
  weight: "~4.5 kg (portable, with battery)",
  screen: '5" capacitive touchscreen',
  probes: "4 probes (FC · pH · Salt/TDS · Temp)",
  strips: "3 top-loaded strip slots · 6 mm (6-in-1 · Phosphate · Magnesium)",
  battery: "14.8 V Li-ion · ~6 h field runtime",
  panel: "Full panel in ~60–90 s (probe + strip develop)",
};

// Definitive continuous probes — no strip needed for these
export const PROBES = [
  { p: "P1", name: "Free Chlorine", method: "Amperostatic (ASP-style true output)", color: "#F59E0B" },
  { p: "P2", name: "pH", method: "Glass ISE electrode", color: "#38BDF8" },
  { p: "P3", name: "Salt / TDS", method: "Conductivity cell", color: "#34D399" },
  { p: "P4", name: "Temperature", method: "RTD in-line", color: "#A78BFA" },
];

// Strip Port A — universal 6-in-1 strip, read by optical reader A
export const STRIP_A = {
  port: "A",
  label: "6-in-1 universal strip",
  reads: [
    { t: "Total Chlorine", note: "→ Combined Cl = Total − Free (from probe)" },
    { t: "Total Alkalinity", note: "buffer capacity" },
    { t: "Cyanuric Acid", note: "stabiliser" },
    { t: "Total / Calcium Hardness", note: "scale / corrosion risk" },
    { t: "Free Chlorine pad", note: "cross-check vs FC probe (drift self-diagnostic)" },
    { t: "pH pad", note: "cross-check vs pH probe (drift self-diagnostic)" },
  ],
};

// Strip Port B — dedicated phosphate strip, read by optical reader B
export const STRIP_B = {
  port: "B",
  label: "Phosphate strip (dedicated)",
  reads: [{ t: "Phosphate", note: "not available on universal strips — needs its own pad" }],
};

// Strip Slot C — dedicated Magnesium / Mineral strip, read by optical reader C
export const STRIP_C = {
  port: "C",
  label: "Magnesium / Mineral strip (dedicated)",
  reads: [{ t: "Magnesium", note: "for mineral pools — the salt/TDS probe reads total conductivity and can't tell magnesium apart from salt" }],
};

// Smart strip detection
export const STRIP_LOGIC = [
  { case: "Slot A (6-in-1)", out: "The 6 core parameters + all 4 probe values." },
  { case: "+ Slot B (Phosphate)", out: "Adds phosphate for problem / algae-prone water." },
  { case: "+ Slot C (Magnesium)", out: "Adds magnesium for mineral / magnesium pools — the salt probe alone can't separate it." },
  { case: "Any mix / none", out: "Each of the 3 slots is sensed independently; the panel reports exactly the strips loaded, always with the 4 probe values. No strips = probe-only quick read." },
];

// Client-profile guided prompt — the tester lights the slots the operator must load
export const STRIP_PROMPT = [
  { profile: "Standard pool", lights: ["A"], note: "Every test — the 6-in-1 covers the core panel." },
  { profile: "Leafy / phosphate-prone area", lights: ["A", "B"], note: "Adds the Phosphate strip to catch the algae fuel." },
  { profile: "Mineral / magnesium pool", lights: ["A", "C"], note: "Adds the Magnesium strip the salt probe can't resolve." },
  { profile: "Mineral pool in a leafy area", lights: ["A", "B", "C"], note: "All three slot lights come on." },
];

// Two build variants — differ only in rinse/waste handling
export const VARIANTS = [
  {
    key: "counter",
    name: "Countertop",
    tag: "In-store bench unit",
    rinse: "Tap-plumbed rinse inlet (¼\" push-fit)",
    waste: "Gravity drain outlet → sink / basin",
    power: "Mains powered (battery optional)",
    note: "Set once on the bench: plumb the rinse line to a tap and the drain to a sink. Fully hands-off flushing between customers.",
    color: "#0891B2",
  },
  {
    key: "portable",
    name: "Portable",
    tag: "Mobile / field unit",
    rinse: "1.5 L refillable rinse bottle (internal)",
    waste: "1.5 L sealed waste bottle (internal)",
    power: "Li-ion battery · ~6 h",
    note: "Fully self-contained for site visits: top up the 1.5 L rinse bottle, empty the waste bottle at the end of the round. Bottles are sized up so the flood-rinse still gives a full day of tests. No plumbing needed.",
    color: "#7C3AED",
  },
];

// Full numbered component list → function (exploded view + table)
export const COMPONENTS = [
  { n: "1", name: "Front outer shell", fn: "UV-stable matte ASA cosmetic housing", grp: "enclosure" },
  { n: "2", name: "Rear chassis / wet-dry divider", fn: "Mounts subassemblies; isolates wet path from electronics", grp: "enclosure" },
  { n: "3", name: '5" touchscreen + cover glass', fn: "Operator UI, live results, report handoff", grp: "ux" },
  { n: "4", name: "Aqua status LED strip", fn: "Good / Adjusting / Attention at a glance", grp: "ux" },
  { n: "5", name: "Spill-proof top catch basin", fn: "Smooth spill-proof bowl (no spout) + beaker notch; drains via center hole; flood-rinsed & self-cleaning between tests", grp: "measure" },
  { n: "6", name: "Overflow-weir sample tank", fn: "Fills from the basin; probes + strip pads sit submerged; overflow weir self-cleans", grp: "measure" },
  { n: "7", name: "Probe block (4 probes) — removable", fn: "FC (amperostatic), pH, Salt/TDS, Temperature; pulls out via the service hatch for cleaning, then self-calibrates on fresh tap water", grp: "measure" },
  { n: "8", name: "Top strip slot A + optical reader", fn: "6 mm slot; strip drops into the tank submerged; RGB pad reflectance read in place", grp: "measure" },
  { n: "9", name: "Top strip slot B + optical reader", fn: "6 mm slot; dedicated Phosphate strip; independent submerged optics", grp: "measure" },
  { n: "10", name: "Strip-present detect sensors ×3", fn: "Senses 1–3 strips; sets which results to report", grp: "measure" },
  { n: "11", name: "Flush / flood-rinse pump", fn: "Drains the tank, then floods rinse water up to overflow-clean the pour basin", grp: "fluidics" },
  { n: "12", name: "Rinse source", fn: "Counter: tap inlet + solenoid · Portable: 1.5 L refillable rinse bottle + pump", grp: "fluidics" },
  { n: "13", name: "Waste path", fn: "Counter: gravity drain outlet · Portable: 1.5 L sealed waste bottle + level float", grp: "fluidics" },
  { n: "14", name: "Main control PCB / MCU", fn: "Sequencing, chemistry engine, storage", grp: "electronics" },
  { n: "15", name: "Analog front-end + optical driver", fn: "Probe conditioning + strip-reader LED/photodiode array", grp: "electronics" },
  { n: "16", name: "Comms — USB-C + Bluetooth", fn: "Results to shop PC; branded report print", grp: "electronics" },
  { n: "17", name: "Rechargeable Li-ion battery pack", fn: "Portable field operation", grp: "electronics" },
  { n: "18", name: "Power inlet / charger board", fn: "Mains → DC, battery charging", grp: "electronics" },
  { n: "19", name: "Rear maintenance cover", fn: "Tool service access to boards & fluidics", grp: "enclosure" },
  { n: "20", name: "Non-slip base feet", fn: "Bench stability", grp: "enclosure" },
  { n: "21", name: "Top strip slot C + optical reader", fn: "6 mm slot; dedicated Magnesium / Mineral strip; independent submerged optics", grp: "measure" },
  { n: "22", name: "Basin flood-rinse gutter", fn: "Hidden perimeter gutter; catches the flood-rinse overspill from the basin and routes it to waste — the basin self-cleans with no lid or moving parts", grp: "fluidics" },
  { n: "23", name: "Strip-prompt LEDs ×3", fn: "One indicator light in front of each slot; lights the strips the operator must insert based on the client's stored pool profile", grp: "ux" },
  { n: "24", name: "Probe service hatch + seated sensor", fn: "Tool-free hatch to remove the probe block for regular cleaning; a seated sensor tells the software when the probe is out, then runs a fresh-tap-water self-calibration on reinsertion", grp: "enclosure" },
];

export const GRP_META = {
  measure: { label: "Measurement", color: "#0891B2" },
  fluidics: { label: "Fluidics (wet)", color: "#0EA5E9" },
  electronics: { label: "Electronics (dry)", color: "#D97706" },
  ux: { label: "Front panel / UX", color: "#7C3AED" },
  enclosure: { label: "Enclosure", color: "#475569" },
};

// The 3-step operator workflow (self-cleaning between customers)
export const WORKFLOW = [
  { t: "Drop the strip(s) in the top slots", d: "Push a 6-in-1 strip into slot A, and — if needed — a Phosphate strip into slot B and a Magnesium strip into slot C. Each 6 mm slot guides the strip straight down into the sample tank; the unit senses which of the three slots are loaded." },
  { t: "Pour the sample", d: "Tip the customer's beaker into the wide spill-proof basin — over-pours just drain back through the center hole. The tank fills until the probes and strip pads are submerged; excess spills over the internal weir." },
  { t: "Probes + strips read", d: "The 4 probes read continuously while the optical readers watch each strip develop. Results appear in ~60–90 s." },
  { t: "Flood self-clean", d: "The sample drains, then the pump floods fresh rinse water back up until it fills and overflows the basin into a hidden perimeter gutter to waste — cleaning the tank AND the pour basin, with no lid or wiping." },
  { t: "Remove & bin the strip", d: "Pull the used strip(s) out of the slot and bin them. Ready for the next test." },
];

export const SERVICE = "Maintenance & calibration: pull the probe block out through the tool-free service hatch to clean it. The unit senses the probe is out and, on reinsertion, asks you to fill the basin with fresh tap water and runs a one-touch self-calibration — no buffer solutions, no downtime.";

const CDN = "https://static.prod-images.emergentagent.com/jobs/a99c2969-7ac0-4b5c-adb1-9cb1c6716fac/images";
export const TESTER_IMG = {
  front: `${CDN}/d02d6ca7adb74f9f9b7d2e59d8a31f2f421454bddcae94cd8dc10f191cd0050c.jpeg`,
  rear: `${CDN}/5f8833f2540a6af9bd73322265c1ea1c08e022513be17bcd4d9bc402994f6fc0.jpeg`,
  top: `${CDN}/c9b1e80388170f7cbfc9215c29eb55d86d13ddad0f5ad31f0241cfb4998caa2e.jpeg`,
  side: `${CDN}/0eafe8d78ec606dde5033e549c2e6a0d7ff7a8c15ac0640e877c86c265f74461.jpeg`,
};

// Founder / contact details for the brief, teaser and outreach email
export const CONTACT = {
  company: "Aqualux Nexus",
  name: "Kevin Marksteiner",
  email: "kevinmarksteiner@gmail.com",
  phone: "+61 418 827 555",
  phoneRaw: "+61418827555",
  location: "Newcastle, NSW, Australia",
  docRef: "RFQ-ANX-PST-001",
};

// The phased build path — the bigger opportunity for a partner
export const ROADMAP = [
  { step: "1", name: "Pool Shop Water Tester", d: "Produce the in-store benchtop unit first — the flagship. Prove the probes, optical strip reading and self-clean in real shops." },
  { step: "2", name: "Mobile Water Tester", d: "Once Step 1 is tested and proven, package the same core into the portable field unit for pool-service technicians." },
  { step: "3", name: "Automated Pool Dosing System", d: "Then progress to the installed on-site system that tests and doses the pool automatically — the biggest prize." },
];

// Honest commercial framing — no invented forecasts; quoting bands + real go-to-market path
export const COMMERCIAL = {
  rollout: "Go-to-market: prove the product in Australia first, then scale into the USA — a far larger market — and onward globally.",
  bandsNote: "The volumes below are quoting bands for unit-cost guidance only — not sales forecasts. We'd value your indicative cost at each stage.",
  bands: [
    { k: "Prototype", v: "1 unit" },
    { k: "Pilot", v: "5–10 units" },
    { k: "Initial run", v: "~100 units" },
    { k: "Scale", v: "1,000+ units" },
  ],
  price: "Retail pricing to be set by the market, positioned to beat the per-test running cost of disk-based systems.",
};
